import { yupResolver } from '@hookform/resolvers/yup';
import { Add } from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';
import { Box, Paper } from '@mui/material';
import { addDays, format } from 'date-fns';
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { usePageTitle } from '../../../main-app/hooks/usePageTitle';
import { langKeys } from '../../../main-app/localization/formErrors';
import { validators } from '../../../main-app/utils/validators';
import { ConnectedInvoice } from '../../components/ConnectedInvoice';
import { PageLayout } from '../../layout/PageLayout';
import { useLeadInvoiceStore } from '../../store/leadStore';
import { useSafeNavigation } from '../../../main-app/hooks/useSafeNavigation';

export const MainPage = () => {
  usePageTitle('Faktura');

  const { safeNavigate } = useSafeNavigation();
  const { setLeadInvoiceState } = useLeadInvoiceStore();

  const onSubmitData = async (data: LeadInvoice) => {
    setLeadInvoiceState(data);
    safeNavigate('/summary');
  };

  const formMethods = useForm<LeadInvoice>({
    resolver: yupResolver(invoiceValidationSchema),
    defaultValues: {
      number: format(new Date(), 'yyyyMMdd'),
      issueDate: new Date(),
      dueDate: addDays(new Date(), 14),
      currency: 'czk',
      variableSymbol: format(new Date(), 'yyyyMMdd'),
      taxPayer: false,
      items: [
        {
          name: '',
          quantity: 1,
          pricePerUnit: 0,
          priceSum: 0,
          unit: 'ks',
        },
      ],
      vendor: {
        registry: 'Fyzická osoba zapsaná v živnostenském rejstříku',
      },
    },
  });

  return (
    <PageLayout>
      <Paper sx={{ p: { xs: 2, sm: 4, md: 6 } }}>
        <FormProvider {...formMethods}>
          <ConnectedInvoice id={'invoice-form'} onSubmit={formMethods.handleSubmit(onSubmitData)} />
        </FormProvider>
      </Paper>
      <Box display={'flex'} justifyContent={'center'} mt={6}>
        <LoadingButton startIcon={<Add />} size={'large'} variant={'contained'} type={'submit'} form={'invoice-form'}>
          Vytvořit fakturu
        </LoadingButton>
      </Box>
    </PageLayout>
  );
};

export type LeadInvoice = {
  number: string;
  amount: number;
  currency: string;
  issueDate?: Date | string;
  dueDate?: Date | string;
  bankAccount?: string;
  bankNumber?: string;
  iban?: string;
  swift?: string;
  variableSymbol: string;
  customer: {
    name: string | null;
    vatId?: string | null;
    taxId?: string | null;
    address?: string | null;
    email?: string | null;
    phoneNumber?: string | null;
  };
  vendor: {
    vatId: string;
    name: string;
    address: string;
    registry: string;
    taxId?: string | null;
    email?: string | null;
    phoneNumber?: string | null;
  };
  items: {
    name: string;
    quantity: number;
    priceSum: number | string;
    pricePerUnit: number | string;
    vatRate?: number | string | null;
    priceWithoutVat?: number | string | null;
    unit?: string | null;
  }[];
  taxPayer: boolean;
  amountWithoutVat?: number | null;
  amountVat?: number | null;
  taxPoint?: string | null;
};

export const invoiceValidationSchema: yup.ObjectSchema<LeadInvoice> = yup.object().shape(
  {
    number: yup.string().required(),
    amount: yup.number().required().min(1),
    currency: yup.string().required(),
    taxPayer: yup.boolean().required(),
    amountWithoutVat: yup
      .number()
      .transform((value, originalValue) => {
        if (originalValue === null || originalValue === undefined || originalValue === '') {
          return undefined;
        }
        return value;
      })
      .when('taxPayer', {
        is: true,
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.nullable(),
      }),
    amountVat: yup
      .number()
      .transform((value, originalValue) => {
        if (originalValue === null || originalValue === undefined || originalValue === '') {
          return undefined;
        }
        return value;
      })
      .when('taxPayer', {
        is: true,
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.nullable(),
      }),
    issueDate: yup.string().required(),
    dueDate: yup.string().required(),
    taxPoint: yup
      .string()
      .nullable()
      .when('taxPayer', {
        is: true,
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.nullable(),
      }),
    bankAccount: yup
      .string()
      .when('iban', {
        is: (val: string | undefined) => !!val,
        then: (s) => s.notRequired(),
        otherwise: (s) => s.required(),
      })
      .test({
        name: 'isBankAccount',
        message: langKeys.invalidBankAccount,
        test: (value) => (!value ? true : /^(\d{1,6}-)?\d{1,10}$/.test(value)),
      }),
    bankNumber: yup
      .string()
      .when('iban', {
        is: (val: string | undefined) => !!val,
        then: (s) => s.notRequired(),
        otherwise: (s) => s.required(),
      })
      .test({
        name: 'isBankNumber',
        message: langKeys.invalidBankNumber,
        test: (value) => (!value ? true : /^\d{4}$/.test(value)),
      }),
    iban: yup.string().when('bankAccount', {
      is: (val: string | undefined) => !!val,
      then: (s) => s.notRequired(),
      otherwise: (s) => s.required(),
    }),
    swift: yup.string(),
    variableSymbol: yup.string().required(),
    customer: yup.object().shape({
      name: yup.string().required(),
      address: yup.string(),
      email: yup.string().matches(validators.email, {
        message: langKeys.invalid,
        excludeEmptyString: true,
      }),
      phoneNumber: yup.string().matches(validators.phone, {
        message: langKeys.invalidPhone,
        excludeEmptyString: true,
      }),
      vatId: yup.string(),
      taxId: yup.string(),
    }),
    vendor: yup.object().shape({
      vatId: yup.string().required(),
      name: yup.string().required(),
      address: yup.string().required(),
      registry: yup.string().required(),
      email: yup.string().matches(validators.email, {
        message: langKeys.invalid,
        excludeEmptyString: true,
      }),
      phoneNumber: yup.string().matches(validators.phone, {
        message: langKeys.invalidPhone,
        excludeEmptyString: true,
      }),
      taxId: yup
        .string()
        .nullable()
        .test({
          name: 'conditionallyRequiredPriceWithoutVat',
          message: langKeys.required,
          test: function (value) {
            // Get the parent form's taxPayer value
            const { from } = this;
            // Make sure 'from' exists and has at least 2 elements
            if (!from || from.length < 2) return true;

            // Navigate up to root form to get taxPayer
            const taxPayer = from[1].value.taxPayer;

            // If taxPayer is true, priceWithoutVat is required
            if (taxPayer === true) {
              return value != null && value !== '';
            }

            // Not required if not a tax payer
            return true;
          },
        }),
    }),
    items: yup
      .array()
      .of(
        yup.object().shape({
          name: yup.string().required(),
          quantity: yup.number().required(),
          pricePerUnit: yup.number().required(),
          priceSum: yup.number().required(),
          vatRate: yup
            .string()
            .nullable()
            .test({
              name: 'conditionallyRequiredVatRate',
              message: langKeys.required,
              test: function (value) {
                // Get the parent form's taxPayer value
                const { from } = this;
                // Make sure 'from' exists and has at least 2 elements
                if (!from || from.length < 2) return true;

                // Navigate up to root form to get taxPayer
                const taxPayer = from[1].value.taxPayer;

                // If taxPayer is true, vatRate is required
                if (taxPayer === true) {
                  return value != null && value !== '';
                }

                // Not required if not a tax payer
                return true;
              },
            }),
          priceWithoutVat: yup
            .string()
            .nullable()
            .test({
              name: 'conditionallyRequiredPriceWithoutVat',
              message: langKeys.required,
              test: function (value) {
                // Get the parent form's taxPayer value
                const { from } = this;
                // Make sure 'from' exists and has at least 2 elements
                if (!from || from.length < 2) return true;

                // Navigate up to root form to get taxPayer
                const taxPayer = from[1].value.taxPayer;

                // If taxPayer is true, priceWithoutVat is required
                if (taxPayer === true) {
                  return value != null && value !== '';
                }

                // Not required if not a tax payer
                return true;
              },
            }),
          unit: yup.string(),
        }),
      )
      .required(),
  },
  [['bankAccount', 'iban']],
);
